import { del, get, keys, set, type UseStore } from 'idb-keyval';

import { DEFAULT_CACHE_MAX_AGE, IDB_DATABASE_NAME } from '../constants.js';

import { createPortalStore } from './idbStore.js';

// ─── Types ──────────────────────────────────────────────────

export interface BlobStorageConfig {
    portalName: string;
    /** Max age for blobs before GC can remove them (default: 7 days) */
    maxAge?: number;
}

interface BlobEntry {
    blob: Blob;
    mimeType: string;
    storedAt: number;
    /** Optional association for GC tracking (e.g., 'equipment:moulin') */
    associatedWith?: string;
}

// ─── Singleton cache ────────────────────────────────────────

const instances = new Map<string, BlobStorage>();

// ─── UUID v4 detection ──────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ─── Factory ────────────────────────────────────────────────

/**
 * Creates (or retrieves a cached) blob storage instance for a portal.
 *
 * Blobs are stored in a dedicated `blob-store` object store within the
 * portal's IndexedDB database.  Each blob is keyed by a UUID and wrapped
 * in a `BlobEntry` that includes the MIME type and creation timestamp.
 */
export function createBlobStorage(config: BlobStorageConfig): BlobStorage {
    const cached = instances.get(config.portalName);
    if (cached) return cached;

    const { portalName, maxAge = DEFAULT_CACHE_MAX_AGE } = config;
    const idbStore: UseStore = createPortalStore(`${IDB_DATABASE_NAME}-${portalName}`, 'blob-store');

    function blobKey(id: string): string {
        return `blob:${id}`;
    }

    /** Store a Blob and return its UUID. */
    async function storeBlob(blob: Blob, associatedWith?: string): Promise<string> {
        const id = crypto.randomUUID();
        const entry: BlobEntry = {
            blob,
            mimeType: blob.type,
            storedAt: Date.now(),
            associatedWith,
        };
        await set(blobKey(id), entry, idbStore);
        return id;
    }

    /** Retrieve a Blob by UUID. */
    async function getBlob(id: string): Promise<Blob | null> {
        try {
            const entry = await get<BlobEntry>(blobKey(id), idbStore);
            return entry?.blob ?? null;
        } catch {
            return null;
        }
    }

    /** Convert a stored blob to a base64 data URL. */
    async function toDataUrl(id: string): Promise<string | null> {
        const blob = await getBlob(id);
        if (!blob) return null;
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /** Delete a single blob. */
    async function removeBlob(id: string): Promise<void> {
        try {
            await del(blobKey(id), idbStore);
        } catch {
            // Silently ignore – blob may already be gone
        }
    }

    /** Delete multiple blobs. */
    async function removeMany(ids: string[]): Promise<void> {
        for (const id of ids) {
            await removeBlob(id);
        }
    }

    /** Remove blobs older than `maxAge`. Returns count of removed entries. */
    async function garbageCollect(): Promise<number> {
        let removed = 0;
        try {
            const allKeys = await keys<string>(idbStore);
            const blobKeys = allKeys.filter((k) => k.startsWith('blob:'));

            for (const key of blobKeys) {
                const entry = await get<BlobEntry>(key, idbStore);
                if (entry && Date.now() - entry.storedAt > maxAge) {
                    await del(key, idbStore);
                    removed++;
                }
            }
        } catch {
            // GC is best-effort
        }
        return removed;
    }

    /** Returns true if the value looks like a UUID v4 reference. */
    function isBlobReference(value: string): boolean {
        return UUID_RE.test(value);
    }

    const instance: BlobStorage = {
        storeBlob,
        getBlob,
        toDataUrl,
        removeBlob,
        removeMany,
        garbageCollect,
        isBlobReference,
        portalName,
    };

    instances.set(portalName, instance);
    return instance;
}

export interface BlobStorage {
    storeBlob: (blob: Blob, associatedWith?: string) => Promise<string>;
    getBlob: (id: string) => Promise<Blob | null>;
    toDataUrl: (id: string) => Promise<string | null>;
    removeBlob: (id: string) => Promise<void>;
    removeMany: (ids: string[]) => Promise<void>;
    garbageCollect: () => Promise<number>;
    isBlobReference: (value: string) => boolean;
    portalName: string;
}
